import api from '../utils/api';

export interface Vendor {
  vnd_id: number;
  vnd_code: string;
  company_name: string;
  contact_person: string | null;
  mobile: string | null;
  email: string | null;
  city: string | null;
  payment_terms: string | null;
  rating: number | null;
}

export interface IndentItem {
  item_id: number;
  indent_id: number;
  mat_id: number;
  mat_code: string;
  mat_name: string;
  category: string;
  sub_category: string;
  variant_id: number | null;
  plan_material_req_id: number | null;
  required_qty: number;
  unit: string;
  rate_per_unit: number;
  total_value: number;
  delivered_qty: number | null;
  pending_qty: number | null;
  notes: string | null;
}

export interface Indent {
  indent_id: number;
  indent_code: string;
  project_id: number;
  project_code?: string;
  project_name?: string;
  supplier_id: number;
  supplier_name: string;
  supplier_contact?: string;
  supplier_mobile?: string;
  supplier_email?: string;
  supplier_gstin?: string;
  supplier_address?: string;
  indent_date: string;
  required_date: string;
  status: 'DRAFT' | 'SENT' | 'ACKNOWLEDGED' | 'PARTIALLY_DELIVERED' | 'DELIVERED' | 'CANCELLED';
  total_items: number;
  total_value: number;
  supplier_notified_at: string | null;
  notified_via: 'EMAIL' | 'WHATSAPP' | 'BOTH' | null;
  supplier_acknowledged_at: string | null;
  delivery_address: string | null;
  notes: string | null;
  created_at: string;
  items?: IndentItem[];
}

export interface PlanRequirement {
  req_id: number;
  plan_id: number;
  mat_id: number;
  mat_code: string;
  mat_name: string;
  category: string;
  required_qty: number;
  unit: string;
  qty_with_wastage: number | null;
  required_by_date: string | null;
  supplier_rate: number | null;
  brand_name: string | null;
  notes: string | null;
}

export interface MaterialResult {
  mat_id: number;
  mat_code: string;
  mat_name: string;
  category: string;
  sub_category: string;
  unit_of_measure: string;
}

// Vendors
export const getVendors = () => api.get('/material-indent/vendors').then((r: any) => r.data.data as Vendor[]);
export const createVendor = (data: Partial<Vendor>) => api.post('/material-indent/vendors', data).then((r: any) => r.data.data as Vendor);

// Indents
export const getIndents = (projectId: number) =>
  api.get('/material-indent', { params: { project_id: projectId } }).then((r: any) => r.data.data as Indent[]);

export const getIndentById = (id: number) =>
  api.get(`/material-indent/${id}`).then((r: any) => r.data.data as Indent);

export const createIndent = (payload: {
  supplier_id: number;
  project_id: number;
  required_date: string;
  indent_date?: string;
  delivery_address?: string;
  notes?: string;
  items: Array<{
    mat_id: number;
    required_qty: number;
    unit: string;
    rate_per_unit: number;
    plan_material_req_id?: number;
    notes?: string;
  }>;
}) => api.post('/material-indent', payload).then((r: any) => r.data.data as Indent);

export const deleteIndent = (id: number) => api.delete(`/material-indent/${id}`);

export const updateStatus = (id: number, status: string) =>
  api.patch(`/material-indent/${id}/status`, { status }).then((r: any) => r.data.data);

export const notifySupplier = (id: number, notified_via: 'EMAIL' | 'WHATSAPP' | 'BOTH') =>
  api.patch(`/material-indent/${id}/notify`, { notified_via }).then((r: any) => r.data.data);

export const acknowledgeIndent = (id: number) =>
  api.patch(`/material-indent/${id}/acknowledge`).then((r: any) => r.data.data);

// Items
export const addItem = (indentId: number, item: Partial<IndentItem>) =>
  api.post(`/material-indent/${indentId}/items`, item).then((r: any) => r.data.data);

export const removeItem = (indentId: number, itemId: number) =>
  api.delete(`/material-indent/${indentId}/items/${itemId}`);

// Plan requirements
export const getPlanRequirements = (projectId: number) =>
  api.get('/material-indent/plan-requirements', { params: { project_id: projectId } }).then((r: any) => r.data.data as PlanRequirement[]);

// Material search
export const searchMaterials = (q: string) =>
  api.get('/material-indent/materials/search', { params: { q } }).then((r: any) => r.data.data as MaterialResult[]);

// Variants
export const getVariantsByMaterial = (matId: number) =>
  api.get('/material-indent/variants', { params: { mat_id: matId } }).then((r: any) => r.data.data);

// Supplier rates
export const getSupplierRatesForVariant = (variantId: number) =>
  api.get('/material-indent/supplier-rates', { params: { variant_id: variantId } }).then((r: any) => r.data.data);
export const getSupplierRatesForVendor = (vendorId: number) =>
  api.get(`/material-indent/vendors/${vendorId}/rates`).then((r: any) => r.data.data);
export const getBestSupplierForMaterial = (matId: number) =>
  api.get('/material-indent/materials/best-supplier', { params: { mat_id: matId } }).then((r: any) => r.data.data);
export const upsertSupplierRate = (data: any) =>
  api.post('/material-indent/supplier-rates', data).then((r: any) => r.data.data);
export const deleteSupplierRate = (rateId: number) =>
  api.delete(`/material-indent/supplier-rates/${rateId}`);

// Supplier contact config
export const getSupplierContactConfig = (supplierId: number) =>
  api.get(`/material-indent/vendors/${supplierId}/contact-config`).then((r: any) => r.data.data);
export const upsertSupplierContactConfig = (data: any) =>
  api.post('/material-indent/vendors/contact-config', data).then((r: any) => r.data.data);
